/**
 * Reusable Fullscreen toggle button + wrapper.
 *
 * Usage:
 *   const { isFullscreen, toggle, FullscreenWrap } = useFullscreen();
 *   return (
 *     <FullscreenWrap>
 *       <FullscreenButton isFullscreen={isFullscreen} onToggle={toggle} />
 *       ... content ...
 *     </FullscreenWrap>
 *   );
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';

/* ─── Button ─── */
export const FullscreenButton: React.FC<{
  isFullscreen: boolean;
  onToggle: () => void;
}> = ({ isFullscreen, onToggle }) => (
  <Tooltip title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen'}>
    <IconButton size="small" onClick={onToggle}>
      {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
    </IconButton>
  </Tooltip>
);

/* ─── Hook ─── */
export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!isFullscreen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isFullscreen]);

  const toggle = useCallback(() => setIsFullscreen((v) => !v), []);

  // Memoize the wrapper component so its identity is stable across renders.
  // Defining it inline (without useCallback) created a fresh component type
  // on every render, which forced React to unmount and remount the entire
  // subtree on every parent state change — blowing away input focus,
  // selection, and scroll position (e.g. editing the transfer qty in
  // Stock Transfer would lose focus after every keystroke / spinner click).
  const FullscreenWrap = useCallback<React.FC<{ children: React.ReactNode }>>(
    ({ children }) =>
      isFullscreen ? (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300, bgcolor: 'var(--color-surface)', p: 3, overflow: 'auto' }}>
          {children}
        </Box>
      ) : (
        <>{children}</>
      ),
    [isFullscreen]
  );

  return { isFullscreen, toggle, setIsFullscreen, FullscreenWrap };
}

export default FullscreenButton;
